import React, { createContext, useContext, useState, useEffect, useMemo, ReactNode } from 'react';

interface BankingVisibilityContextType {
  showValues: boolean;
  toggleVisibility: () => void;
}

const BankingVisibilityContext = createContext<BankingVisibilityContextType | undefined>(undefined);

const STORAGE_KEY = 'banking-values-visibility';

export const BankingVisibilityProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [showValues, setShowValues] = useState<boolean>(() => {
    // Initialize from localStorage, default to true (show values)
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored !== null ? stored === 'true' : true;
  });

  const toggleVisibility = () => {
    setShowValues((prev) => {
      const newValue = !prev;
      localStorage.setItem(STORAGE_KEY, String(newValue));
      return newValue;
    });
  };

  useEffect(() => {
    // Sync with localStorage changes from other tabs
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY && e.newValue !== null) {
        setShowValues(e.newValue === 'true');
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const value = useMemo(() => ({ showValues, toggleVisibility }), [showValues]);

  return <BankingVisibilityContext.Provider value={value}>{children}</BankingVisibilityContext.Provider>;
};

export const useBankingVisibility = (): BankingVisibilityContextType => {
  const context = useContext(BankingVisibilityContext);
  if (!context) {
    throw new Error('useBankingVisibility must be used within BankingVisibilityProvider');
  }
  return context;
};
